import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ApiError, apiErrorCode, apiErrorMessage } from '../../lib/api';
import {
  createWikiPage,
  slugify,
  WIKI_SLUG_RE,
  wikiRoute,
  type WikiPageFull,
  type WikiPageSlim,
  type WikiSpaceRef,
} from '../../lib/wiki';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  space: WikiSpaceRef;
  spaceName: string;
  /** Parent page, or null for the space root. */
  parent: WikiPageSlim | null;
  siblings: WikiPageSlim[];
  onCreated: (page: WikiPageFull) => void;
};

/** Screen 6: title drives the slug; space and parent are shown, not typed. */
export default function CreatePageDialog({ open, onOpenChange, space, spaceName, parent, siblings, onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTitle('');
      setSlug('');
      setSlugTouched(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(title));
  }, [title, slugTouched]);

  const takenSlugs = useMemo(() => new Set(siblings.map((page) => page.slug)), [siblings]);
  const slugValid = WIKI_SLUG_RE.test(slug) && slug.length <= 64;
  const slugTaken = takenSlugs.has(slug);
  const canSubmit = title.trim().length > 0 && slugValid && !slugTaken && !busy;
  const previewPath = parent ? `${parent.path}/${slug || '…'}` : slug || '…';

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const page = await createWikiPage(space, {
        parent_id: parent?.id ?? null,
        slug,
        title: title.trim(),
        body_md: '',
      });
      onOpenChange(false);
      onCreated(page);
    } catch (err) {
      if (err instanceof ApiError && apiErrorCode(err) === 'CONFLICT') {
        setError('A page with this slug already exists here. Choose another slug.');
      } else {
        setError(apiErrorMessage(err, 'Failed to create page.'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create page</DialogTitle>
        </DialogHeader>
        <dl className="grid grid-cols-[5rem_1fr] gap-y-1 text-sm">
          <dt className="text-muted-foreground">Space</dt>
          <dd className="truncate">{spaceName}</dd>
          <dt className="text-muted-foreground">Parent</dt>
          <dd className="truncate">{parent ? parent.path.split('/').join(' › ') : 'Space root'}</dd>
        </dl>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="wiki-create-title">Title</Label>
            <Input
              id="wiki-create-title"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wiki-create-slug">Slug</Label>
            <Input
              id="wiki-create-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              maxLength={64}
              aria-invalid={slug.length > 0 && (!slugValid || slugTaken)}
            />
            <p className="text-xs text-muted-foreground">
              {slug.length > 0 && !slugValid
                ? 'Use a-z, 0-9 and single dashes.'
                : slugTaken
                  ? 'Already used by a sibling page.'
                  : 'a-z, 0-9 and dashes · unique among siblings'}
            </p>
          </div>
          <p className="truncate text-xs text-muted-foreground" title={wikiRoute(space, previewPath)}>
            URL {wikiRoute(space, previewPath)}
          </p>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={() => void submit()}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
